/** Single source of truth for identity, navigation and biography content. */
import { articles } from "./articles";

export const site = {
	name: "Boateng Jephthah Agyenim",
	shortName: "Jephthah Boateng",
	initials: "JB",
	role: "Senior Full-Stack Engineer",
	email: "boatengjephthah27@gmail.com",
	phone: "+233 55 007 4433",
	availability: "Open to selected work for 2026",
	tagline: "Systems that hold under load.",
	intro:
		"I build secure, high-performance web systems end-to-end — typed APIs, event-driven data pipelines, and the interfaces that sit on top of them. Django, FastAPI, Node and NestJS on the back; React, Next.js and TypeScript on the front; AWS, Docker and CI/CD underneath.",
};

export const navLinks = [
	{ name: "Index", link: "/" },
	{ name: "Work", link: "/projects" },
	{ name: "Writing", link: "/blog" },
	{ name: "Profile", link: "/profile" },
	{ name: "Capabilities", link: "/skills" },
	{ name: "Contact", link: "/contact" },
];

export const socialLinks = [
	{
		name: "LinkedIn",
		handle: "in/jephthah-boateng",
		url: "https://www.linkedin.com/in/jephthah-boateng/",
	},
	{
		name: "GitHub",
		handle: "@boatengjephthah27",
		url: "https://github.com/boatengjephthah27",
	},
	{ name: "Email", handle: site.email, url: `mailto:${site.email}` },
	{ name: "Telephone", handle: site.phone, url: "tel:+233550074433" },
];

export const stats = [
	{ value: 8, suffix: "+", label: "Production systems" },
	{ value: 7, suffix: "", label: "Years shipping" },
	{ value: 3, suffix: "", label: "Engineering teams led" },
	{ value: articles.length, suffix: "", label: "Essays published" },
];

export const marqueeWords = [
	"Full-Stack Engineering",
	"Distributed Systems",
	"API Architecture",
	"Data Pipelines",
	"DevOps & Cloud",
	"Design Systems",
];

export const services = [
	{
		index: 1,
		title: "Backend & API Engineering",
		summary:
			"Django, FastAPI, NestJS and Node services with typed boundaries, explicit auth and failure modes that are designed rather than discovered.",
		detail:
			"Multi-tenant RBAC enforced on the server rather than hinted at in the client. Idempotent writes so a retried job is a no-op instead of a duplicate. Background work on Celery and queue workers, with per-channel error isolation so one failing integration never takes the request down with it.",
		capabilities: [
			"REST & GraphQL APIs",
			"Multi-tenant RBAC",
			"OAuth 2.0 & JWT",
			"Celery & background workers",
			"Idempotent backfills",
		],
	},
	{
		index: 2,
		title: "Data Pipelines & Integrations",
		summary:
			"Event-driven pipelines that reconcile third-party platforms against a warehouse nightly, and survive the APIs changing underneath them.",
		detail:
			"I have built attribution pipelines spanning Meta, LinkedIn and Simpli.fi, multi-CRM segment sync against HubSpot and GoHighLevel, and nightly re-resync jobs that capture every add, edit and removal since the last run — each with a full audit trail per execution.",
		capabilities: [
			"Meta / LinkedIn / Simpli.fi APIs",
			"HubSpot & GoHighLevel sync",
			"GA4 Measurement Protocol",
			"CRON scheduling",
			"Audit trails & reconciliation",
		],
	},
	{
		index: 3,
		title: "Frontend Architecture",
		summary:
			"React and Next.js front-ends built as a system: tokens, primitives and documentation rather than four teams inventing four buttons.",
		detail:
			"Typed data layers with React Query, state that lives where it belongs, accessibility handled once in the primitive instead of re-argued per feature, and performance budgets enforced in CI rather than negotiated after launch.",
		capabilities: [
			"React & Next.js",
			"TypeScript",
			"Design systems",
			"React Query & Redux",
			"Accessible UI",
		],
	},
	{
		index: 4,
		title: "DevOps & Platform",
		summary:
			"The unglamorous layer: containers, pipelines, observability, and a cost line that does not quietly triple.",
		detail:
			"AWS across EC2, S3, RDS, Lambda and CloudFront. Docker and Kubernetes for reproducible environments, GitHub Actions for deploys that are boring by design, Nginx at the edge and Grafana so an incident starts with a graph instead of a guess.",
		capabilities: [
			"AWS (EC2, S3, RDS, Lambda)",
			"Docker & Kubernetes",
			"GitHub Actions CI/CD",
			"Nginx & Linux",
			"Grafana observability",
		],
	},
];

export const toolkit = [
	{
		group: "Languages",
		items: ["Python", "TypeScript", "JavaScript", "SQL", "HTML", "CSS"],
	},
	{
		group: "Backend",
		items: ["Django", "FastAPI", "NestJS", "Node.js", "Celery", "Strapi 5"],
	},
	{
		group: "Frontend",
		items: ["React", "Next.js", "Vue.js", "Tailwind", "React Query", "Redux"],
	},
	{
		group: "Data",
		items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "GraphQL", "REST"],
	},
	{
		group: "Cloud & DevOps",
		items: [
			"AWS",
			"Docker",
			"Kubernetes",
			"GitHub Actions",
			"Nginx",
			"Grafana",
		],
	},
	{
		group: "Tools",
		items: ["Git", "Postman", "Figma", "Jira", "VS Code", "Copilot"],
	},
];

export const experience = [
	{
		period: "Feb 2026 — Present",
		role: "Senior Full-Stack Engineer",
		org: "Lepta",
		summary:
			"Collaboration platform work: real-time document presence, an installable PWA, native payments, and an access model that spans organisation, workspace and project.",
		points: [
			"Built an installable PWA with a service worker, Web Push subscriptions and a dedicated @mentions inbox with per-tab unread counts",
			"Shipped push delivery with in-app and email fanout for offline users, using best-effort per-channel error isolation",
			"Implemented a WebSocket hub for live collaboration — presence, cursors, comments and reactions — over typed pub/sub channels with viewer-role recomputation on broadcast",
			"Built role-based access control across organisation → workspace → project with server-enforced permission gates and a usePermissions hook on the client",
			"Integrated Lepta Pay with custom checkout and subscription payment URL wiring",
			"Diagnosed and fixed a single-worker uvicorn self-loopback deadlock in the re-extract endpoint by short-circuiting our own public base URL to disk reads",
		],
	},
	{
		period: "Dec 2025 — Present",
		role: "Senior Full-Stack Engineer",
		org: "Hyro Exchange",
		summary:
			"Platform and integration architecture for a digital asset exchange, where the bugs that matter are the ones that touch money.",
		points: [
			"Contribute to application and service integration architecture alongside the Lead Engineer, implementing to agreed technical standards",
			"Review code delivered by external development partners before acceptance, and integrate accepted work into the platform",
			"Build and maintain cloud infrastructure, automation and deployment systems including CI/CD pipelines and containerised environments",
			"Share QA responsibility across the team, with particular care on money flows: deposits, trades, withdrawals and balances",
		],
	},
	{
		period: "Dec 2024 — Dec 2025",
		role: "Lead Engineer",
		org: "Path3 Media",
		summary:
			"Led the engineering behind a multi-platform advertising operations system — attribution, autonomous budget allocation and multi-CRM audience sync.",
		points: [
			"Migrated daily performance reporting from campaign-aggregate to ad-level attribution across three ad platforms, eliminating duplicated spend rows and enabling per-creative CPME/CPI optimisation",
			"Shipped an autonomous budget optimiser that reallocates daily budgets across Meta, LinkedIn and Simpli.fi on CPME/CPI, with a SHADOW→LIVE rollout and a full audit trail per run",
			"Applied EMA smoothing, exploration and diversity floors, dark-horse rotations and per-platform min/max clamps across OPEN and CLOSED budget scopes",
			"Implemented a multi-CRM segment-sync poller with a v2 dbquery runner, plus a nightly re-resync capturing every add, edit and removal since the last run",
			"Cut manual media-ops work by automating multi-platform deployment, go-dark and nightly audience resync across organisations",
		],
	},
	{
		period: "Dec 2022 — Dec 2025",
		role: "Frontend Engineer",
		org: "MassEnergize",
		summary:
			"Multi-tenant climate campaign platform serving many communities running concurrent campaigns from isolated workspaces on shared infrastructure.",
		points: [
			"Built and maintained a multi-tenant campaign management platform supporting multiple communities and concurrent campaigns",
			"Developed production-grade, responsive dashboards for creating, managing, monitoring and optimising campaigns and climate actions",
			"Engineered a reusable frontend architecture and component library that standardised campaign creation, reporting and administrative workflows",
			"Built administrative portals covering role-based access control, organisation and community administration, user permissions, campaign approvals, audit history and operational reporting",
		],
	},
	{
		period: "Jun 2021 — Dec 2022",
		role: "Software Engineer",
		org: "SkilledHQ LLC",
		summary:
			"Marketplace operations: the moderation, payout and notification machinery that keeps a multi-vendor storefront honest.",
		points: [
			"Built an admin product moderation system — toggle products active or inactive, require deactivation reasons, notify vendors with actionable messages, and block vendor reactivation until admin review",
			"Designed a vendor payout approval workflow with an admin review dashboard, cancel and retry mechanics, and automatic tier promotion after successful payouts",
			"Built a transactional email system on Brevo with 15+ templated emails across the order lifecycle — confirmation, shipping, delivery, payout and RTO — dispatched via Celery async tasks",
			"Implemented FCM push notifications with the Web Push API for real-time order updates, delivery status changes and admin actions",
			"Implemented Google Analytics environment isolation so tracking only fires in production",
		],
	},
];

export const education = [
	{
		period: "2017 — 2021",
		role: "BSc Engineering",
		org: "Kwame Nkrumah University of Science and Technology",
		summary:
			"An engineering degree whose useful half was computational: programming, numerical methods and the mathematics that makes a model trustworthy.",
		points: [
			"Relevant coursework: Python programming, numerical methods, linear algebra, functional and mathematical analysis, time series analysis",
			"Grounding in error analysis and measurement — why a result without an uncertainty attached is not a result",
		],
	},
	{
		period: "Ongoing",
		role: "Continued Study",
		org: "Self-directed",
		summary:
			"Distributed systems, database internals, and the parts of security that only become obvious after an incident.",
		points: [
			"Data structures, algorithms and relational modelling",
			"Cloud architecture, containerisation and CI/CD practice",
		],
	},
];

export const languages = [
	{ name: "English", level: "Fluent" },
	{ name: "Twi", level: "Native" },
	{ name: "French", level: "Writing & Reading" },
];

export const principles = [
	{
		title: "Constraints first",
		body: "Work produced without constraint is indistinguishable from work produced without thought. I start by finding the real limits — the rate cap, the budget, the database nobody is allowed to change.",
	},
	{
		title: "Decisions over outputs",
		body: "Screenshots are the residue of thinking. What matters is what was tried, what was rejected and why — so I document the reasoning, not just the result.",
	},
	{
		title: "Correct, then fast",
		body: "A pipeline that is quick and wrong costs more than one that is slow and right. I make it provable first — idempotent, audited, replayable — and only then make it quick.",
	},
	{
		title: "Boring deploys",
		body: "Shipping should be the least interesting part of the week. Containers, pipelines and rollbacks exist so that the excitement stays in the product rather than the release.",
	},
];
